/**
 * Staff Analytics Page - ROI, Productivity & Flight Risk
 */
import { useState, useEffect } from 'react';
import { 
  Users, TrendingUp, TrendingDown, DollarSign, AlertTriangle, Award,
  Activity, Target, BarChart3, User, Clock, Zap
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import axios from 'axios';
import HROverviewTab from '../components/hr/HROverviewTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444'];

const StaffAnalyticsPage = () => {
  const [roiData, setRoiData] = useState(null);
  const [flightRisk, setFlightRisk] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Lazy-load per tab: fetch each endpoint only when its tab is first activated.
  // Overview tab is self-contained in HROverviewTab — no need to hit ROI/flight/trend on mount.
  useEffect(() => {
    const fetchFor = async () => {
      if (activeTab === 'overview') return;
      if (activeTab === 'roi' && roiData) return;
      if (activeTab === 'flight-risk' && flightRisk) return;
      if (activeTab === 'trend' && trend) return;

      setLoading(true);
      try {
        if (activeTab === 'roi' && !roiData) {
          const r = await axios.get(`${API}/staff-analytics/roi`, { withCredentials: true });
          setRoiData(r.data);
        } else if (activeTab === 'flight-risk' && !flightRisk) {
          const r = await axios.get(`${API}/staff-analytics/flight-risk`, { withCredentials: true });
          setFlightRisk(r.data);
        } else if (activeTab === 'trend' && !trend) {
          const r = await axios.get(`${API}/staff-analytics/productivity-trend`, { withCredentials: true });
          setTrend(r.data);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  if (loading && activeTab !== 'overview') return <div className="flex justify-center py-20" data-testid="staff-analytics-loading"><div className="animate-spin h-10 w-10 border-b-2 border-red-700 rounded-full"></div></div>;

  const summary = roiData?.summary || {};
  const perfData = [
    { name: 'Excellent', value: roiData?.by_performance?.excellent || 0, color: '#10B981' },
    { name: 'Good', value: roiData?.by_performance?.good || 0, color: '#3B82F6' },
    { name: 'Average', value: roiData?.by_performance?.average || 0, color: '#F59E0B' },
    { name: 'Underperforming', value: roiData?.by_performance?.underperforming || 0, color: '#EF4444' }
  ].filter(d => d.value > 0);

  const riskData = [
    { name: 'Minimal', value: flightRisk?.summary?.minimal_risk || 0, color: '#10B981' },
    { name: 'Low', value: flightRisk?.summary?.low_risk || 0, color: '#3B82F6' },
    { name: 'Medium', value: flightRisk?.summary?.medium_risk || 0, color: '#F59E0B' },
    { name: 'High', value: flightRisk?.summary?.high_risk || 0, color: '#EF4444' }
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="staff-analytics-page">
      {/* Header + summary strip only shown for legacy tabs (ROI / Flight Risk / Trend).
          The Overview tab has its own richer hero inside HROverviewTab. */}
      {activeTab !== 'overview' && (
        <>
          <div className="bg-gradient-to-r from-purple-900 to-purple-700 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-3">
                  <Activity className="w-8 h-8" /> Staff Analytics
                </h1>
                <p className="text-purple-200 text-sm mt-1">ROI • Productivity • Flight Risk Analysis</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-purple-200">Avg Productivity Score</p>
                <p className="text-3xl font-bold">{summary.avg_productivity_score || 0}/100</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="bg-white rounded-xl p-4 border shadow-sm min-w-0">
              <p className="text-xs text-gray-500">Total Staff</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-800">{summary.total_staff || 0}</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 min-w-0">
              <p className="text-xs text-emerald-600">Revenue/Staff</p>
              <p className="text-lg sm:text-2xl font-bold text-emerald-700 truncate">RM{(summary.revenue_per_staff || 0).toLocaleString()}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 border border-red-100 min-w-0">
              <p className="text-xs text-red-600">Cost/Staff</p>
              <p className="text-lg sm:text-2xl font-bold text-red-700 truncate">RM{(summary.cost_per_staff || 0).toLocaleString()}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 min-w-0">
              <p className="text-xs text-blue-600">Avg ROI</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-700">{summary.avg_roi || 0}%</p>
            </div>
            <div className={`rounded-xl p-4 border min-w-0 ${summary.vs_benchmark >= 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
              <p className={`text-xs ${summary.vs_benchmark >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>vs Benchmark</p>
              <p className={`text-xl sm:text-2xl font-bold ${summary.vs_benchmark >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {summary.vs_benchmark >= 0 ? '+' : ''}{summary.vs_benchmark || 0}%
              </p>
            </div>
          </div>
        </>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto" data-testid="staff-analytics-tabs">
        {['overview', 'roi', 'flight-risk', 'trend'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`tab-${tab}`}
            className={`px-4 py-2 font-medium text-sm transition-all whitespace-nowrap ${
              activeTab === tab ? 'border-b-2 border-red-700 text-red-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'overview' ? 'Ringkasan HR' : tab === 'roi' ? 'ROI Analysis' : tab === 'flight-risk' ? 'Flight Risk' : 'Productivity Trend'}
          </button>
        ))}
      </div>

      {/* HR Overview Tab - consolidated data from ALL HR modules */}
      {activeTab === 'overview' && <HROverviewTab />}

      {/* ROI Tab */}
      {activeTab === 'roi' && (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Performance Pie */}
            <div className="bg-white rounded-xl p-6 border shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4">Performance Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={perfData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {perfData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Performers */}
            <div className="bg-white rounded-xl p-6 border shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" /> Top Performers
              </h3>
              <div className="space-y-3">
                {(roiData?.top_performers || []).map((staff, idx) => (
                  <div key={staff.staff_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                      <div>
                        <p className="font-medium text-gray-800">{staff.name}</p>
                        <p className="text-xs text-gray-500">{staff.position}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">{staff.roi_percent}% ROI</p>
                      <p className="text-xs text-gray-500">Score: {staff.productivity_score}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Needs Attention */}
          {roiData?.needs_attention?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-bold text-amber-700 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Staff Perlu Perhatian
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roiData.needs_attention.map(staff => (
                  <div key={staff.staff_id} className="bg-white rounded-lg p-4 border border-amber-200">
                    <p className="font-semibold text-gray-800">{staff.name}</p>
                    <p className="text-sm text-gray-500">{staff.position}</p>
                    <div className="mt-2 flex justify-between items-center">
                      <span className={`px-2 py-1 rounded text-xs ${
                        staff.performance_status === 'average' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {staff.performance_status}
                      </span>
                      <span className="text-sm font-bold">{staff.roi_percent}% ROI</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Staff Table */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <h3 className="font-bold text-gray-800 mb-4">All Staff ROI</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Staff</th>
                    <th className="text-left py-3 px-2">Position</th>
                    <th className="text-right py-3 px-2">Salary</th>
                    <th className="text-right py-3 px-2">Total Cost</th>
                    <th className="text-right py-3 px-2">Revenue Contribution</th>
                    <th className="text-right py-3 px-2">ROI</th>
                    <th className="text-center py-3 px-2">Score</th>
                    <th className="text-center py-3 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(roiData?.all_staff || []).map(staff => (
                    <tr key={staff.staff_id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-2 font-medium">{staff.name}</td>
                      <td className="py-3 px-2 text-gray-500">{staff.position}</td>
                      <td className="py-3 px-2 text-right">RM{staff.gross_salary?.toLocaleString()}</td>
                      <td className="py-3 px-2 text-right">RM{staff.total_employer_cost?.toLocaleString()}</td>
                      <td className="py-3 px-2 text-right">${staff.revenue_contribution?.toLocaleString()}</td>
                      <td className={`py-3 px-2 text-right font-bold ${staff.roi_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {staff.roi_percent}%
                      </td>
                      <td className="py-3 px-2 text-center">{staff.productivity_score}</td>
                      <td className="py-3 px-2 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          staff.performance_status === 'excellent' ? 'bg-emerald-100 text-emerald-700' :
                          staff.performance_status === 'good' ? 'bg-blue-100 text-blue-700' :
                          staff.performance_status === 'average' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {staff.performance_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Flight Risk Tab */}
      {activeTab === 'flight-risk' && (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Risk Pie */}
            <div className="bg-white rounded-xl p-6 border shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4">Flight Risk Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={riskData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {riskData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* High Risk List */}
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <h3 className="font-bold text-red-700 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> High Flight Risk Staff
              </h3>
              {(flightRisk?.high_risk_staff || []).length > 0 ? (
                <div className="space-y-3">
                  {flightRisk.high_risk_staff.map(staff => (
                    <div key={staff.staff_id} className="bg-white rounded-lg p-4 border border-red-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-800">{staff.name}</p>
                          <p className="text-xs text-gray-500">{staff.position} • {staff.years_of_service} years</p>
                        </div>
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-bold">
                          Risk: {staff.risk_score}%
                        </span>
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-400">Risk Factors:</p>
                        <ul className="mt-1 space-y-1">
                          {staff.risk_factors.map((factor, i) => (
                            <li key={i} className="text-xs text-red-600">• {factor}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Tiada high risk staff</p>
              )}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="font-bold text-blue-700 mb-4">💡 Cadangan Retention</h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {(flightRisk?.recommendations || []).map((rec, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-blue-800">
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs">{i+1}</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Trend Tab */}
      {activeTab === 'trend' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <h3 className="font-bold text-gray-800 mb-4">Productivity Trend (6 Months)</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend?.trend || []}>
                  <defs>
                    <linearGradient id="colorProductivity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="productivity_ratio" stroke="#8B5CF6" fillOpacity={1} fill="url(#colorProductivity)" name="Productivity Ratio" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4">Revenue vs Payroll</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trend?.trend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="revenue" fill="#10B981" name="Revenue ($)" />
                    <Bar dataKey="payroll" fill="#EF4444" name="Payroll (RM)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4">Analysis</h3>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${trend?.analysis?.trend_direction === 'improving' ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <p className="text-sm font-medium text-gray-700">Trend Direction</p>
                  <p className={`text-lg font-bold ${trend?.analysis?.trend_direction === 'improving' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {trend?.analysis?.trend_direction === 'improving' ? '📈 Improving' : '📉 Declining'}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Best Month</p>
                  <p className="text-lg font-bold text-blue-600">{trend?.analysis?.best_month || '-'}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Worst Month</p>
                  <p className="text-lg font-bold text-amber-600">{trend?.analysis?.worst_month || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffAnalyticsPage;
